import { createFileRoute } from '@tanstack/react-router'
import { Ban, Plus, X } from 'lucide-react'
import { useId, useMemo, useState, type ReactElement } from 'react'
import { z } from 'zod'

import { DataTablePagination } from '~/components/data/DataTablePagination'
import {
  DiscountDialog,
  DiscountsTable,
  subscriptionsKeys,
  useCreateDiscount,
  useRevokeDiscount,
  type DiscountRow,
} from '~/features/subscriptions'
import { http } from '~/lib/http'
import { logger } from '~/lib/logger'
import {
  toPaginatedResult,
  usePaginatedQuery,
  type PaginatedParams,
  type PaginatedResult,
} from '~/lib/query/use-paginated-query'
import { useUrlState } from '~/lib/query/use-url-state'

/**
 * `/admin/subscriptions/discounts` — Discount Codes (URL-paginated).
 *
 * URL search keys: `page`, `limit`, `status`.
 */
const discountsSearchSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(10).max(100).default(25),
  status: z.enum(['all', 'active', 'paused', 'revoked']).default('active'),
})
type DiscountsSearch = z.infer<typeof discountsSearchSchema>

const URL_DEFAULTS: DiscountsSearch = {
  page: 1,
  limit: 25,
  status: 'active',
}

export const Route = createFileRoute('/admin/subscriptions/discounts')({
  validateSearch: discountsSearchSchema,
  component: DiscountsPage,
})

interface DiscountsListPayload {
  readonly discounts?: readonly DiscountRow[]
  readonly items?: readonly DiscountRow[]
  readonly total?: number
  readonly pagination?: {
    readonly page?: number
    readonly limit?: number
    readonly total?: number
    readonly totalPages?: number
  }
}

async function fetchDiscountsPage(
  params: PaginatedParams,
): Promise<PaginatedResult<DiscountRow>> {
  const query: Record<string, string | number | undefined> = {
    page: params.page,
    limit: params.limit,
  }
  if (params.sort) query.sort = params.sort
  if (params.filters?.status && params.filters.status !== 'all') {
    query.status = params.filters.status
  }

  try {
    const raw = await http.get<DiscountsListPayload>(
      '/api/v1/admin/subscriptions/discounts',
      { query },
    )
    const rows = (raw.discounts ?? raw.items ?? []) as readonly DiscountRow[]
    const total = raw.pagination?.total ?? raw.total ?? rows.length
    return toPaginatedResult(rows, total, params, raw.pagination)
  } catch {
    // Endpoint not yet deployed in every env — keep parity with the
    // graceful-degrade behavior in features/subscriptions/queries.
    return toPaginatedResult<DiscountRow>([], 0, params)
  }
}

function DiscountsPage(): ReactElement {
  const search = Route.useSearch()
  const [, setUrl] = useUrlState(discountsSearchSchema, URL_DEFAULTS)

  const createMutation = useCreateDiscount()
  const revokeMutation = useRevokeDiscount()
  const [createOpen, setCreateOpen] = useState(false)
  const [confirmingRevoke, setConfirmingRevoke] = useState<DiscountRow | null>(
    null,
  )

  const params: PaginatedParams = {
    page: search.page,
    limit: search.limit,
    filters: { status: search.status },
  }

  const discounts = usePaginatedQuery<DiscountRow>({
    queryKey: [...subscriptionsKeys.discounts(), 'paginated'],
    params,
    fetcher: fetchDiscountsPage,
  })

  const all = useMemo(() => discounts.data?.data ?? [], [discounts.data])
  // Defensive: the discounts endpoint may not server-filter status yet.
  const filtered = useMemo(() => {
    if (search.status === 'all') return all
    return all.filter((d) => d.status === search.status)
  }, [all, search.status])

  const counts = useMemo(() => {
    const active = all.filter((d) => d.status === 'active').length
    const usedThisMonth = all.reduce((sum, d) => sum + d.used_count, 0)
    return { active, usedThisMonth }
  }, [all])

  const pagination = discounts.data?.pagination

  const handleRevoke = (discount: DiscountRow): void => {
    setConfirmingRevoke(discount)
  }

  const handleConfirmRevoke = (discount: DiscountRow): void => {
    revokeMutation.mutate(
      { id: discount.id },
      {
        onSuccess: () => setConfirmingRevoke(null),
        onError: (cause) => logger.warn('discount revoke failed', { cause }),
      },
    )
  }

  return (
    <div className="ka-subs-list-page">
      <header className="ka-page-header">
        <div>
          <h1 className="ka-page-header-title">Discount Codes</h1>
          <p className="ka-page-header-subtitle">
            {counts.active} active · {counts.usedThisMonth} used this month
          </p>
        </div>
        <div className="ka-page-header-actions">
          <button
            type="button"
            className="ka-btn ka-btn--primary"
            onClick={() => setCreateOpen(true)}
          >
            <Plus size={14} aria-hidden="true" /> New Discount
          </button>
        </div>
      </header>

      <div className="ka-filter-strip" role="search">
        <div className="ka-filter-group" role="group" aria-label="Status">
          {(['all', 'active', 'paused', 'revoked'] as const).map((s) => (
            <button
              key={s}
              type="button"
              className={`ka-detail-chip${search.status === s ? ' is-active' : ''}`}
              aria-pressed={search.status === s}
              onClick={() => setUrl({ status: s, page: 1 })}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <DiscountsTable
        discounts={filtered}
        isLoading={discounts.isPending}
        error={discounts.error}
        hasFilters={search.status !== 'all'}
        onClearFilters={() => setUrl({ status: 'all', page: 1 })}
        onCreate={() => setCreateOpen(true)}
        onRevoke={handleRevoke}
      />
      <DataTablePagination
        className="ka-subs-list-pagination"
        page={pagination?.page ?? search.page}
        limit={pagination?.limit ?? search.limit}
        total={pagination?.total ?? filtered.length}
        isLoading={discounts.isPending}
        onChange={({ page, limit }) => setUrl({ page, limit })}
      />

      {createOpen ? (
        <DiscountDialog
          isSubmitting={createMutation.isPending}
          onClose={() => {
            setCreateOpen(false)
            createMutation.reset()
          }}
          onSubmit={(input) => {
            createMutation.mutate(input, {
              onSuccess: () => setCreateOpen(false),
              onError: (cause) =>
                logger.warn('discount create failed', { cause }),
            })
          }}
        />
      ) : null}

      {confirmingRevoke !== null ? (
        <RevokeDiscountConfirmDialog
          discount={confirmingRevoke}
          isSubmitting={revokeMutation.isPending}
          onCancel={() => setConfirmingRevoke(null)}
          onConfirm={() => handleConfirmRevoke(confirmingRevoke)}
        />
      ) : null}
    </div>
  )
}

interface RevokeDiscountConfirmDialogProps {
  readonly discount: DiscountRow
  readonly isSubmitting: boolean
  readonly onCancel: () => void
  readonly onConfirm: () => void
}

/**
 * Inline typed-confirm dialog for revoking a discount code.
 *
 * Mirrors the destructive-action pattern used by RevokeSubscriptionDialog —
 * the admin must type `REVOKE <code>` before the destructive button enables.
 */
function RevokeDiscountConfirmDialog({
  discount,
  isSubmitting,
  onCancel,
  onConfirm,
}: RevokeDiscountConfirmDialogProps): ReactElement {
  const titleId = useId()
  const confirmFieldId = useId()
  const [confirmText, setConfirmText] = useState<string>('')

  const expectedConfirm = `REVOKE ${discount.code}`
  const canSubmit = confirmText === expectedConfirm && !isSubmitting

  return (
    <div
      className="ka-modal-backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel()
      }}
    >
      <div
        className="ka-modal is-sm"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header className="ka-modal-head">
          <div
            className="ka-modal-head-icon is-destructive"
            aria-hidden="true"
          >
            <Ban size={16} />
          </div>
          <h2 className="ka-modal-head-title" id={titleId}>
            Revoke discount · {discount.code}
          </h2>
          <button
            type="button"
            className="ka-navbar-icon-btn"
            onClick={onCancel}
            aria-label="Close dialog"
          >
            <X size={16} />
          </button>
        </header>

        <div className="ka-modal-body">
          <div className="ka-banner is-destructive">
            Revoking this discount removes it from future redemptions. Existing
            uses are preserved for audit.
          </div>

          <div className="ka-typed-confirm">
            <label
              className="ka-typed-confirm-label"
              htmlFor={confirmFieldId}
            >
              Type <code>{expectedConfirm}</code> to confirm:
            </label>
            <div className="ka-field-input-wrap">
              <input
                id={confirmFieldId}
                className="ka-field-input"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={expectedConfirm}
                autoCapitalize="off"
                autoComplete="off"
                spellCheck={false}
              />
            </div>
          </div>
        </div>

        <footer className="ka-modal-foot">
          <button
            type="button"
            className="ka-btn ka-btn--ghost"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="ka-btn ka-btn--destructive"
            disabled={!canSubmit}
            onClick={onConfirm}
          >
            <Ban size={14} aria-hidden="true" /> Revoke
          </button>
        </footer>
      </div>
    </div>
  )
}
